import { type ReactElement } from 'react'
import { isCompanyId, isEmpty, isRoleId, isTeamId, type DashboardDataWithEditable } from '@terros/common'
import { Badge, Group, Menu, Stack, Text, Tooltip } from '@mantine/core'
import { useTranslation } from '@front/model'
import { MaterialSymbolIcon, TButton } from '@components'

type Props = {
  dashboard: DashboardDataWithEditable
  /** Whether this is the one Home is currently surfacing. */
  isSurfaced: boolean
  isCopying: boolean
  deleting: boolean
  onCopy: (dashboard: DashboardDataWithEditable) => void
  onDelete: (dashboard: DashboardDataWithEditable) => void
}

/**
 * A hustle widget handed down from a broader scope. Only one hustle widget ever
 * surfaces on Home, so the ones that don't are marked with a crossed-out eye —
 * otherwise a viewer with three of these has no way to tell which they see.
 */
export function InheritedHustleRow({
  dashboard,
  isSurfaced,
  isCopying,
  deleting,
  onCopy,
  onDelete,
}: Props): ReactElement {
  const { t } = useTranslation()
  const name = dashboard.name?.trim()

  return (
    <Group className='border-default-border p-2xl rounded-xl border' gap='sm' wrap='nowrap'>
      <Stack className='min-w-0 flex-1' gap={2}>
        <Text fw={500} truncate>
          {isEmpty(name) ? t('Hustle Widget') : name}
        </Text>
        <Text c='dimmed' size='sm'>
          {t(toHustleSourceLabel(dashboard.scopeId))}
        </Text>
      </Stack>
      <Badge color='neutral' size='sm' variant='light'>
        {t('Shared')}
      </Badge>
      <Tooltip label={isSurfaced ? t('Shown on your Home') : t('Not shown on your Home')}>
        <MaterialSymbolIcon icon={isSurfaced ? 'visibility' : 'visibility_off'} size='md' />
      </Tooltip>
      <Menu position='bottom-end' shadow='md' width={220}>
        <Menu.Target>
          <TButton
            buttonType='icon-rounded'
            icon='more_vert'
            loading={isCopying || deleting}
            tooltip='Hustle actions'
            variant='ghost'
          />
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<MaterialSymbolIcon icon='content_copy' size='md' />}
            onClick={() => onCopy(dashboard)}
          >
            {t('Use as Mine')}
          </Menu.Item>
          {dashboard.editable === true && (
            <Menu.Item
              color='red'
              leftSection={<MaterialSymbolIcon icon='delete' size='md' />}
              onClick={() => onDelete(dashboard)}
            >
              {t('Delete')}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  )
}

function toHustleSourceLabel(scopeId: DashboardDataWithEditable['scopeId']): string {
  if (isTeamId(scopeId)) return 'From your team'
  if (isRoleId(scopeId)) return 'From your role'
  if (isCompanyId(scopeId)) return 'From your company'
  return 'Shared with you'
}
